import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Info, Loader2 } from "lucide-react";
import { api, imgUrl } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { SeriesCard } from "../components/SeriesCard";
import { EpisodeCard } from "../components/EpisodeCard";
import { HorizontalRow } from "../components/HorizontalRow";
import { ContinueCard } from "../components/ContinueCard";
import { getContinueList, removeProgress } from "../lib/progress";
import { Logo } from "../components/Logo";

const Empty = () => (
  <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
    <Logo size="text-4xl" className="mb-4" />
    <p className="text-white/50 max-w-md">No series yet. Head to the admin panel to add your first original AI series.</p>
    <a href="/admin" data-testid="empty-admin-link" className="mt-6 px-6 py-3 rounded-full bg-blood text-white font-semibold hover:bg-blood-hover transition-colors">Open Admin</a>
  </div>
);

export default function Home() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [continueList, setContinueList] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/home").then((r) => setData(r.data)).finally(() => setLoading(false));
    setContinueList(getContinueList());
  }, []);

  const removeContinue = (epId) => {
    removeProgress(epId);
    setContinueList(getContinueList());
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="animate-spin text-blood" size={40} />
      </div>
    );

  if (!data?.hero) {
    return (<div className="min-h-screen grain"><Navbar /><Empty /></div>);
  }

  const hero = data.hero;
  const playHero = async () => {
    const s = await api.get(`/series/${hero.id}`);
    const ep = s.data.episodes?.[0];
    if (ep) navigate(`/watch/${ep.id}`);
    else navigate(`/series/${hero.id}`);
  };

  return (
    <div className="min-h-screen grain pb-24 md:pb-12">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-end" data-testid="hero-banner">
        <div className="absolute inset-0">
          {hero.banner_url || hero.poster_url ? (
            <img src={imgUrl(hero.banner_url || hero.poster_url)} alt={hero.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#1a0005] to-black" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-black/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-[1600px] w-full mx-auto px-4 sm:px-8 pb-16 pt-28">
          <p className="text-xs tracking-[0.3em] uppercase font-bold text-blood mb-3 glow-red">Original AI Series</p>
          <h1 className="font-display font-black text-4xl sm:text-6xl md:text-7xl tracking-tighter text-white max-w-3xl leading-[0.95]">
            {hero.title}
          </h1>
          {hero.genres?.length > 0 && (
            <p className="mt-4 text-sm text-white/60 font-semibold tracking-wide">{hero.genres.join("  ·  ")}{hero.year ? `  ·  ${hero.year}` : ""}</p>
          )}
          <p className="mt-4 text-white/75 max-w-xl text-base leading-relaxed line-clamp-3">{hero.description}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={playHero} data-testid="hero-watch-now-btn" className="flex items-center gap-2 px-8 py-3.5 rounded-full bg-blood text-white font-bold shadow-[0_0_18px_rgba(220,20,60,0.45)] hover:bg-blood-hover hover:shadow-[0_0_28px_rgba(220,20,60,0.65)] transition-all">
              <Play size={20} className="fill-white" /> Watch Now
            </button>
            <button onClick={() => navigate(`/series/${hero.id}`)} data-testid="hero-view-series-btn" className="flex items-center gap-2 px-8 py-3.5 rounded-full bg-white/10 backdrop-blur-md text-white font-bold border border-white/15 hover:bg-white/20 transition-colors">
              <Info size={20} /> View Series
            </button>
          </div>
        </div>
      </section>

      <div className="relative z-10 -mt-8">
        {continueList.length > 0 && (
          <section className="mb-10 sm:mb-14" data-testid="row-continue-watching">
            <div className="flex items-baseline gap-3 px-4 sm:px-8 mb-4">
              <span className="w-6 h-[3px] bg-blood rounded-full glow-box" />
              <h2 className="text-xl sm:text-2xl font-display font-bold text-white">Continue Watching</h2>
            </div>
            <div className="flex overflow-x-auto gap-4 px-4 sm:px-8 pb-4 snap-x snap-mandatory scrollbar-hide">
              {continueList.map((item, i) => (
                <ContinueCard key={item.episodeId} item={item} onRemove={removeContinue} index={i} />
              ))}
            </div>
          </section>
        )}

        {data.featured?.length > 0 && (
          <HorizontalRow title="Featured Series" testId="row-featured">
            {data.featured.map((s, i) => <SeriesCard key={s.id} series={s} index={i} />)}
          </HorizontalRow>
        )}

        {data.latest_episodes?.length > 0 && (
          <section className="mb-10 sm:mb-14" data-testid="row-latest-episodes">
            <div className="flex items-baseline gap-3 px-4 sm:px-8 mb-4">
              <span className="w-6 h-[3px] bg-blood rounded-full glow-box" />
              <h2 className="text-xl sm:text-2xl font-display font-bold text-white">Latest Episodes</h2>
            </div>
            <div className="flex overflow-x-auto gap-4 px-4 sm:px-8 pb-4 snap-x snap-mandatory scrollbar-hide">
              {data.latest_episodes.map((e, i) => (
                <div key={e.id} className="shrink-0 w-80 snap-start"><EpisodeCard episode={e} showSeries index={i} /></div>
              ))}
            </div>
          </section>
        )}

        {data.new_releases?.length > 0 && (
          <HorizontalRow title="New Releases" testId="row-new-releases">
            {data.new_releases.map((s, i) => <SeriesCard key={s.id} series={s} index={i} />)}
          </HorizontalRow>
        )}

        {data.all_series?.length > 0 && (
          <HorizontalRow title="All Series" testId="row-all-series">
            {data.all_series.map((s, i) => <SeriesCard key={s.id} series={s} index={i} />)}
          </HorizontalRow>
        )}
      </div>
    </div>
  );
}
